import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@/components/ui/select';
import { CODE_SUGGESTIONS, formatTag, isChanged, isNonStandardTerm, normalizeValue, validateValue, } from '@/lib/dicom/tagEditForm';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { variesSummary } from './dicomTagText';
/** Radix Select cannot hold an empty value, so "not recorded" travels as this. */
const NONE = '__none__';
/**
 * Plain-language notes for the DICOM attributes whose standard name doesn't say what they hold,
 * or what changing them does.
 */
const ALIAS_KEYS = {
    ResponsiblePerson: 'tagEditor.alias.owner',
    PatientSexNeutered: 'tagEditor.alias.neutered',
    BodyPartExamined: 'tagEditor.alias.bodyPart',
    PatientPosition: 'tagEditor.alias.patientPosition',
};
/** Defined terms whose meaning is spelled out next to the code. */
const TERM_GLOSS_KEYS = {
    PatientSex: 'tagEditor.sex',
    ImageLaterality: 'tagEditor.laterality',
    PatientPosition: 'tagEditor.position',
};
/**
 * One editable value of the DICOM tag editor: the tag (group, element), the DICOM name and value
 * type, and a control fitted to the value type, with what the files said before, what the
 * worklist says, the length budget and any problem under it.
 */
export function DicomTagFieldRow({ field, values, onChange, disabled, today, action, compact = false, matched = false, onMatchedChange, }) {
    const { t } = useTranslation();
    const [focused, setFocused] = useState(false);
    const { def } = field;
    const value = values[field.key] ?? '';
    const valueChanged = isChanged(field, values);
    const changed = valueChanged || (matched && field.varies !== null);
    const problem = validateValue(def, value, today);
    const locked = disabled || !field.readable;
    const hints = fieldHints({
        field,
        value,
        valueChanged,
        locked,
        focused,
        matched,
        onMatchedChange,
        onChange,
        action,
        t,
    });
    const control = (_jsx(FieldControl, { field: field, value: value, onChange: onChange, locked: locked, compact: compact, onFocusChange: setFocused }));
    const showProblem = problem && changed && !locked;
    const hintBlock = (hints.length > 0 || showProblem) && (_jsxs("p", { className: "text-muted-foreground mt-1 text-xs", children: [hints.map((hint, i) => (_jsxs("span", { children: [i > 0 && ' · ', hint.node] }, hint.key))), showProblem && (_jsx("span", { className: "text-destructive block", role: "alert", children: t(`tagEditor.validation.${problem.code}`, problem) }))] }));
    if (compact) {
        return (_jsxs("div", { className: cn('min-w-0', changed && 'border-l-primary border-l-2 pl-2'), "data-testid": `dicom-tag-field-${field.key}`, "data-changed": changed || undefined, children: [_jsxs("p", { className: "text-muted-foreground mb-1 text-xs", children: [_jsx("span", { className: "font-mono", children: formatTag(def.tag) }), ' ', _jsx("span", { className: "text-foreground", children: def.name }), ' ', _jsx("span", { className: "font-mono text-[11px]", children: def.vr })] }), control, hintBlock] }));
    }
    return (_jsxs("div", { className: cn('border-border grid grid-cols-1 gap-x-3 gap-y-1 border-t px-4 py-2 sm:grid-cols-[6.5rem_minmax(0,1fr)_minmax(0,1.5fr)] sm:items-start', changed && 'border-l-primary border-l-2 pl-[14px]'), "data-testid": `dicom-tag-field-${field.key}`, "data-changed": changed || undefined, children: [_jsx("div", { className: "text-muted-foreground font-mono text-xs sm:pt-2", children: formatTag(def.tag) }), _jsxs("div", { className: "text-sm sm:pt-1.5", children: [def.name, _jsx("span", { className: "text-muted-foreground ml-1.5 font-mono text-[11px]", children: def.vr }), ALIAS_KEYS[def.keyword] && (_jsx("span", { className: "text-muted-foreground block text-xs", children: t(ALIAS_KEYS[def.keyword]) }))] }), _jsxs("div", { className: "min-w-0", children: [control, hintBlock] })] }));
}
/**
 * What a row says under its control, in order: unreadable, what it was, how the files disagree
 * (with "Make every file match"), the worklist's copy (with "Use"), a value outside the defined
 * terms, the dialog's fix, and the length budget while typing.
 */
function fieldHints({ field, value, valueChanged, locked, focused, matched, onMatchedChange, onChange, action, t, }) {
    const { def, target } = field;
    const hints = [];
    const hint = (key, node) => hints.push({ key, node });
    if (!field.readable) {
        hint('unreadable', t(target.kind === 'image' ? 'tagEditor.unreadable' : 'tagEditor.unreadableTag'));
    }
    if (valueChanged) {
        hint('was', t('tagEditor.hint.was', { value: field.initial || t('tagEditor.empty') }));
    }
    if (field.varies) {
        hint('varies', t('tagEditor.hint.varies', { values: variesSummary(field.varies, t) }));
        // Keeping the value most files hold is no change, so bringing the rest into line is asked for.
        if (!locked && !valueChanged && onMatchedChange) {
            hint('match', matched ? (_jsxs("span", { children: [t('tagEditor.hint.matching'), ' ', _jsx("button", { type: "button", className: "text-primary hover:underline", onClick: () => onMatchedChange(false), children: t('tagEditor.hint.matchUndo') })] })) : (_jsx("button", { type: "button", className: "text-primary hover:underline", onClick: () => onMatchedChange(true), children: t('tagEditor.hint.matchAll') })));
        }
    }
    const worklist = def.worklistValue;
    if (target.kind === 'study' &&
        def.inWorklist &&
        worklist !== null &&
        worklist.trim() !== '' &&
        normalizeValue(def, worklist) !== normalizeValue(def, value)) {
        hint('worklist', _jsxs("span", { children: [t('tagEditor.hint.worklist', { value: worklist }), ' ', !locked && (_jsx("button", { type: "button", className: "text-primary hover:underline", onClick: () => onChange(worklist), children: t('tagEditor.hint.use') }))] }));
    }
    if (def.definedTerms && isNonStandardTerm(field, value)) {
        hint('nonStandard', t('tagEditor.hint.nonStandard', { value: value.trim(), terms: def.definedTerms.join(', ') }));
    }
    if (action && !locked)
        hint('action', action);
    if ((focused || valueChanged) && def.maxLength < 1000 && def.vr !== 'DA' && !def.definedTerms) {
        hint('length', `${normalizeValue(def, value).length} / ${def.maxLength}`);
    }
    return hints;
}
/**
 * The control fitted to the tag's value type: a list for defined terms, a text area for long
 * text, a date picker for a date, and a text box (with the usual codes offered) otherwise.
 */
function FieldControl({ field, value, onChange, locked, compact, onFocusChange, }) {
    const { t } = useTranslation();
    const { def } = field;
    const ariaLabel = controlLabel(field, t);
    const controlClass = 'h-8 text-sm';
    if (def.definedTerms) {
        // A value the file holds outside the defined terms is offered as itself, or the control
        // would show nothing and hide what the header says.
        const current = normalizeValue(def, value);
        const nonStandard = isNonStandardTerm(field, value) ? current : null;
        const options = [
            ...def.definedTerms,
            ...(nonStandard ? [nonStandard] : []),
            ...(def.required ? [] : ['']),
        ];
        return (_jsxs(Select, { value: current || NONE, onValueChange: (next) => onChange(next === NONE ? '' : next), disabled: locked, children: [_jsx(SelectTrigger, { className: controlClass, "aria-label": ariaLabel, children: _jsx(SelectValue, {}) }), _jsx(SelectContent, { children: options.map((term) => (_jsx(SelectItem, { value: term || NONE, children: !term
                            ? t('tagEditor.notRecorded')
                            : term === nonStandard
                                ? value.trim()
                                : termLabel(def.keyword, term, t) }, term || NONE))) })] }));
    }
    if (def.vr === 'LT') {
        return (_jsx("textarea", { "aria-label": ariaLabel, value: value, onChange: (e) => onChange(e.target.value), onFocus: () => onFocusChange(true), onBlur: () => onFocusChange(false), disabled: locked, rows: compact ? 1 : 2, placeholder: field.readable ? t('tagEditor.noneInFiles') : undefined, className: "border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring min-h-8 w-full resize-y rounded-md border px-3 py-1.5 text-sm focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50" }));
    }
    // A code string (View Position, Body Part Examined) is typed freely but offered the usual
    // codes, and reads in capitals as it will be stored.
    const suggestions = def.vr === 'CS' ? CODE_SUGGESTIONS[def.tag] : undefined;
    const listId = suggestions
        ? `dicom-tag-suggest-${field.key.replace(/[^A-Za-z0-9]/g, '-')}`
        : undefined;
    return (_jsxs(_Fragment, { children: [_jsx(Input, { type: def.vr === 'DA' ? 'date' : 'text', "aria-label": ariaLabel, value: value, onChange: (e) => onChange(e.target.value), onFocus: () => onFocusChange(true), onBlur: () => onFocusChange(false), disabled: locked, maxLength: def.vr === 'DA' ? undefined : def.maxLength, list: listId, autoCapitalize: def.vr === 'CS' ? 'characters' : 'off', autoCorrect: "off", spellCheck: false, className: cn(controlClass, def.vr === 'CS' && 'uppercase') }), suggestions && (_jsx("datalist", { id: listId, children: suggestions.map((code) => (_jsx("option", { value: code }, code))) }))] }));
}
/**
 * The control's accessible name. The series' heading sits above its rows on screen; a screen
 * reader needs it in the label, or every series' "Image 1" sounds the same.
 */
function controlLabel(field, t) {
    const { def, target } = field;
    const imageLabel = target.kind === 'image'
        ? t('tagEditor.imageLabel', { number: target.image.instanceNumber ?? '?' })
        : null;
    const seriesLabel = target.kind === 'study'
        ? null
        : target.series.seriesNumber !== null
            ? t('tagEditor.seriesHeading', { number: target.series.seriesNumber })
            : t('tagEditor.seriesHeadingNoNumber');
    return [seriesLabel, imageLabel, def.name, formatTag(def.tag)].filter(Boolean).join(', ');
}
function termLabel(keyword, term, t) {
    const gloss = TERM_GLOSS_KEYS[keyword];
    return gloss ? `${term} · ${t(`${gloss}.${term}`)}` : term;
}
